import { ChangeEvent, FC, useMemo } from 'react';

import dashboardMultiActionsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/common/dashboard_multi_actions_button_click';
import { Card, Checkbox, Flex, Text, VSpacing } from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';

import { getIsCampaignNeedShowCheckbox, getIsMutatingActionAvailable } from 'components/CampaignsDashboard/utils/utils';

import { setCheckedCampaignIds } from 'store/models/campaignsDashboard';
import { currentAccount } from 'store/models/user';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    selectAll: 'CampaignsDashboard.CampaignsTable.mobile.selectAll',
};

export const SelectAllCheckbox: FC<{ isMobile?: boolean }> = ({ isMobile = false }) => {
    const campaigns = useSelector((state) => state.campaignsDashboard.campaigns);
    const checkedCampaignIds = useSelector((state) => state.campaignsDashboard.checkedCampaignIds);
    const currentAccountStatus = useSelector(currentAccount)?.status;
    const isCampaignsLoading = useSelector((state) => state.campaignsDashboard.isCampaignsLoading);

    const dispatch = useDispatch();
    const trls = useTrls();

    const selectableCampaigns = useMemo(
        () =>
            [...campaigns.values()].filter(({ frontendStatus, budget }) =>
                getIsCampaignNeedShowCheckbox(
                    frontendStatus,
                    budget,
                    getIsMutatingActionAvailable(frontendStatus, currentAccountStatus)
                )
            ),
        [campaigns, currentAccountStatus]
    );

    const maxSelectableCount = selectableCampaigns.length;

    if (maxSelectableCount === 0) {
        return <VSpacing default={32} />;
    }

    const checkedCampaignsCount = checkedCampaignIds.size;
    const selectAllIndeterminate = checkedCampaignsCount > 0 && checkedCampaignsCount < maxSelectableCount;
    const selectAllChecked = checkedCampaignsCount > 0 && checkedCampaignsCount === maxSelectableCount;

    const onSelectAllToggle = (event: ChangeEvent<HTMLInputElement>) => {
        const checked = event.target.checked;
        const newCheckedCampaignIds = new Set<number>();

        if (checked) {
            selectableCampaigns.forEach(({ id: campaignId }) => {
                newCheckedCampaignIds.add(campaignId);
            });
        }

        dashboardMultiActionsButtonClick({
            buttonName: 'dashboard_multi_actions_select_all',
            checkedCampaignsCount: newCheckedCampaignIds.size,
            campaignIds: JSON.stringify([...newCheckedCampaignIds]),
        });
        dispatch(setCheckedCampaignIds(newCheckedCampaignIds));
    };

    const checkbox = (
        <Checkbox
            disabled={isCampaignsLoading}
            checked={selectAllChecked}
            indeterminate={selectAllIndeterminate}
            onChange={onSelectAllToggle}
            data-qa={isMobile ? 'campaigns-mobile-select-all-checkbox' : 'campaigns-table-check-all-checkbox'}
        />
    );

    if (isMobile) {
        return (
            <Card stretched padding={0} borderRadius={12} borderWidth="default" borderStyle="neutral">
                <Flex Element="label" align="center" gap={12} p={16}>
                    {checkbox}
                    <Text typography="label-2-regular">{trls(TrlKeys.selectAll)}</Text>
                </Flex>
            </Card>
        );
    }

    return (
        <Card padding={4} style="primary" borderRadius={8} borderWidth="default">
            <Flex>{checkbox}</Flex>
        </Card>
    );
};
